import {
  GSI_SCRIPT_LOADED,
  API_CALL_SUCCESS,
  CLEAR_AUTH_ERROR, RESET_CUSTOMER_TRAVELLER_SUBMISSION, SET_CUSTOMER_VALUE,
  SET_RECENTLY_USED_AIRPORT_CODE,
  SYNC_USER_DETAILS, API_CALL_FAILURE,
  API_CALL, SAVE_CUSTOMER_SIGNATURE, DELETE_CUSTOMER_SIGNATURE,
  SET_RESOLVED_TRACKING_ID,
} from './actionConstants'
import {
  FETCH_AVAILABLE_CREDIT,
  FETCH_PAYMENT_CARDS,
  USER_LOGIN,
  USER_REGISTRATION,
  UPDATE_USER_DETAILS,
  USER_LOGOUT,
  UPDATE_ACCOUNT_PARTNERSHIP,
  USER_FORGOT_PASSWORD,
  FETCH_STRIPE_PAYMENT_CARDS,
  FETCH_AGENT_HUB_AGENCY_STRIPE_PAYMENT_CARDS,
  CHECK_LEGACY_ORDERS_FOR_CURRENT_USER,
  CHECK_USER_EXISTS,
  USER_RESET_PASSWORD,
  FETCH_CUSTOMER_DETAILS,
  DELETE_CUSTOMER_DETAILS,
  UPDATE_CUSTOMER_DETAILS,
  SEND_VERIFICATION_CODE,
  VERIFY_SMS_CODE,
  SEND_SIGNUP_OTP_CODE,
  VERIFY_SIGNUP_OTP_CODE,
  VALIDATE_REGISTRATION,
  UPDATE_ADD_PHONE_PROMPTED,
  FETCH_CUSTOMER_TRAVELLERS,
  DELETE_CUSTOMER_TRAVELLER,
  SUBMIT_CUSTOMER_TRAVELLER,
  USER_EMAIL_CONFIRMATION,
  FETCH_SIMILAR_ACCOUNTS,
} from './apiActionConstants'
import { getAvailableCredit, getSavedCardsFromStripe, getLedLegacyOrders, getSavedCardsOnStripe } from 'api/order'
import { getAgentHubAgencySavedStripeCards, getSavedStripeCards } from 'api/payment'
import * as authService from 'api/auth'
import {
  setGooglePhoneCollectionAvailable,
  shouldCollectGooglePhoneAfterLogin,
} from 'lib/auth/googlePhoneCollection'
import { sendResetPasswordEmail, resetPassword, requestUserEmailValidation, getSimilarAccounts } from 'api/auth'
import { setTimeUserLastSeenSnackbar } from 'storage/creditsSnackbar'
import { getCreditSnackbar, shouldShowCreditSnackbar } from 'lib/customer/creditSnackbarUtils'
import { replaceWithRegion } from './NavigationActions'
import { deleteCustomerDetails, getCustomerDetails, requestTravelCompanions, updateCustomerDetails, deleteCustomerTraveller, updateCustomerTraveller, createCustomerTraveller } from 'api/traveller'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type ReCAPTCHA from 'react-google-recaptcha'
import { isStripePaymentElementCardAvailable } from 'checkout/selectors/payment/paymentType'
import { isAgentHubAgencySavedCardsEnabled } from 'agentHub/selectors/agentHubSelectors'
import { replace } from 'lib/routing/appRouting'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { OtpDeliveryChannel } from 'constants/otpDelivery'
import type { AppAction } from './types/ActionTypes'
import config from 'constants/config'
import { refreshLabFeatureFlagsForCurrentUser } from './SystemActions'
import { brandHasChertoCustomerBkExperiment } from 'constants/experiments'
import { getStitchedSeedId, stitchedSeedIdCookieName } from 'analytics/snowplow/helpers/stitchedSeedId'
import { getSeedIdFromApi, SEED_ID_REQUEST_TIMEOUT_MS, SEED_ID_TIMEOUT_MS } from 'api/lab'
import { setCookie } from 'lib/web/cookieUtils'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'
import {
  endPostLoginExperimentSync,
  rebucketExperiments,
  trackPostLoginExperimentSyncClientEvent,
} from './ExperimentActions'
import { CHERTO_USER_ID_UNIT_TYPE } from 'lib/experiments/chertoConstants'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { consumeEmailVerificationReturnPath } from 'lib/auth/emailVerificationReturnPath'
import { broadcastEmailVerified } from 'lib/auth/emailVerificationBroadcast'
import { isOidcSession } from 'agentHub/auth/oidc/authMethod'
import { canUseExperimentationOnClient, canUseTrackingIdentityOnClient } from '../consent/waitingForConsent'

const HIGH_VALUE_OFFER_AMOUNT = 4999
const MID_VALUE_OFFER_AMOUNT = 2999

function persistStitchedSeedId(memberId: string, stitchedSeedId: string) {
  if (canUseTrackingIdentityOnClient() && !getStitchedSeedId(memberId)) {
    setCookie(stitchedSeedIdCookieName(memberId), stitchedSeedId, DurationSeconds.ONE_YEAR, '/', 'Lax')
  }
}

function waitForCurrentStackToClear() {
  return new Promise<void>(resolve => {
    setTimeout(resolve, 0)
  })
}

const LOGIN_WAIT_TIMEOUT = Symbol('login-wait-timeout')

function waitForLoginBudget() {
  return new Promise<typeof LOGIN_WAIT_TIMEOUT>(resolve => {
    setTimeout(() => resolve(LOGIN_WAIT_TIMEOUT), SEED_ID_TIMEOUT_MS)
  })
}

async function awaitSeedIdWithLoginBudget(
  seedPromise: Promise<string | undefined>,
): Promise<string | undefined | typeof LOGIN_WAIT_TIMEOUT> {
  return Promise.race([seedPromise, waitForLoginBudget()])
}

function fetchSeedIdForBrowserLogin(memberId: string) {
  if (!canUseTrackingIdentityOnClient()) return Promise.resolve(undefined)
  return getSeedIdFromApi(memberId, { timeout: SEED_ID_REQUEST_TIMEOUT_MS })
}

async function raceSeedIdWithLoginBudget(
  seedPromise: Promise<string | undefined>,
): Promise<{
  seedId?: string
  pendingRequest?: Promise<string | undefined>
}> {
  const attempt = await awaitSeedIdWithLoginBudget(seedPromise)
  if (attempt !== LOGIN_WAIT_TIMEOUT) {
    return { seedId: attempt }
  }

  return { pendingRequest: seedPromise }
}

async function fetchStitchedSeedIdForLogin(memberId: string): Promise<{
  seedId?: string
  pendingRequest?: Promise<string | undefined>
}> {
  const firstAttempt = await raceSeedIdWithLoginBudget(fetchSeedIdForBrowserLogin(memberId))
  if (firstAttempt.seedId) return firstAttempt

  trackPostLoginExperimentSyncClientEvent('seed_id_retry')
  await waitForCurrentStackToClear()

  const retryPromise = firstAttempt.pendingRequest ?? fetchSeedIdForBrowserLogin(memberId)
  return raceSeedIdWithLoginBudget(retryPromise)
}

function persistLateStitchedSeedId(
  getState: () => App.State,
  memberId: string,
  pendingRequest: Promise<string | undefined>,
) {
  pendingRequest.then((seedId) => {
    if (!seedId || !canUseTrackingIdentityOnClient()) return
    if (getState().auth.account.memberId !== memberId) return
    persistStitchedSeedId(memberId, seedId)
    trackPostLoginExperimentSyncClientEvent('seed_id_persisted')
  }).catch((error) => {
    console.error('Failed to persist late stitched seed ID:', error)
  })
}

export function getUserKeyFromPhone(phoneNumber: App.PhoneNumber): string {
  return `+${phoneNumber.prefix}${phoneNumber.phone}`
}

export function checkUserExists(
  user: { email?: string, phoneNumber?: App.PhoneNumber },
  recaptchaApi?: Pick<ReCAPTCHA, 'executeAsync'> & { provider?: authService.CaptchaProvider } | null,
): AppAction {
  return (dispatch, getState) => {
    const key = user.email ?? (user.phoneNumber ? getUserKeyFromPhone(user.phoneNumber) : undefined)
    if (!key) {
      return
    }
    const state = getState()
    // no need to check again, we've already done so
    if (state.auth.users[key]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: CHECK_USER_EXISTS,
      request: async() => {
        let recaptchaData: string | null | undefined
        if (recaptchaApi) {
          recaptchaData = await recaptchaApi.executeAsync()
        }
        return authService.checkUserExists(user, recaptchaData, recaptchaApi?.provider)
      },
      email: user.email,
      phoneNumber: user.phoneNumber,
      key,
    })
  }
}

export function loginNativeApp(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.webViewAccountAccess(state.geo.currentRegionCode, getIsLuxPlusEnabled(state)),
    })
  } }

export function login(
  user: App.User,
  password: string,
  recaptchaData?: string,
  captchaProvider?: authService.CaptchaProvider,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.login(user, password, recaptchaData, state.geo.currentRegionCode, luxPlusEnabled, captchaProvider),
      source: user.email ? 'email' : 'phone',
    })
  }
}

export interface GoogleLoginData {
  id_google: string
  email: string
  token_id: string
  givenName: string
  surname: string
}

export interface GoogleLoginOptions {
  collectPhone?: boolean
}

export function loginGoogle(data: GoogleLoginData, options?: GoogleLoginOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: async() => {
        const result = await authService.loginGoogle(
          data,
          state.geo.currentRegionCode,
          luxPlusEnabled,
        )
        setGooglePhoneCollectionAvailable(shouldCollectGooglePhoneAfterLogin({
          collectPhone: options?.collectPhone,
          isSignUp: result.isSignUp,
          phone: result.account?.phone,
        }), data.id_google, result.account?.memberId)
        return result
      },
      source: 'google',
    })
  }
}

export interface FacebookLoginData {
  app_id: string
  email: string
  access_token: string
  givenName: string
  surname: string
}

export function loginFacebook(data: FacebookLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.loginFacebook(data, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'facebook',
    })
  }
}

export interface AppleLoginData {
  id_token: string
  givenName?: string
  firstName?: string
}

export function loginApple(data: AppleLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.loginApple(data, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'apple',
    })
  }
}

const registerMethods = {
  phone: authService.register,
  email: authService.register,
  google: authService.loginGoogle,
  facebook: authService.loginFacebook,
  apple: authService.loginApple,
}

/**
 * `password` is optional so guest checkout can create a passwordless account —
 * svc-auth only requires an email, and the API layer omits the key when it is
 * undefined. Pair it with `sendWelcomeSetPasswordEmail` so the customer gets a
 * link to set one afterwards.
 *
 * `options.guestCheckout` marks the registration as a silent by-product of a
 * metasearch guest purchase: userMiddleware then skips the marketing/loyalty
 * signup side effects (Braze identify, Société enrolment) and records an
 * explicit marketing opt-out instead.
 */
export function register(
  userData: Partial<App.AuthAccount> & { password?: string, sendWelcomeSetPasswordEmail?: boolean },
  source: App.SignUpAuthSource,
  options: { guestCheckout?: boolean, marketingOptIn?: boolean } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const registerData = {
      ...userData,
      utmAdgroup: state.utm.adgroup,
      utmCampaign: state.utm.campaign,
      utmContent: state.utm.content,
      utmMedium: state.utm.medium,
      utmSource: state.utm.source,
      utmTerm: state.utm.term,
    }

    const regionCode = userData.countryCode ?? state.geo.currentRegionCode

    dispatch({
      type: API_CALL,
      api: USER_REGISTRATION,
      source,
      guestCheckout: options.guestCheckout,
      marketingOptIn: options.marketingOptIn,
      request: () => registerMethods[source]((registerData as any), regionCode),
    })
  }
}

export function verifyUserEmail(token: string, showErrorSnackbar = true): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: USER_EMAIL_CONFIRMATION,
      request: async() => {
        const intl = getCurrentIntl()
        const verifyUserErrorMessage = intl.formatMessage({
          id: 'GSywTyP75zLJcG2Q0PEJ',
          defaultMessage: 'We couldn’t verify your email, please try again.',
          description: 'Error snackbar message shown when user email verification fails',
        })
        try {
          await requestUserEmailValidation({ token, brand: config.BRAND })

          showSnackbar(intl.formatMessage({
            id: '-zxB05oL_raYF3rm3I3i',
            defaultMessage: 'Your email has been successfully verified.',
            description: 'Success snackbar message shown after email verification completes',
          }), 'success')
          dispatch(syncUserDetails({ emailVerified: true }))
          // Lets the customer's other open tabs (e.g. the one they asked to verify from) catch up
          broadcastEmailVerified()
          dispatch(replaceWithRegion(consumeEmailVerificationReturnPath() ?? '/'))
        } catch {
          if (showErrorSnackbar) showSnackbar(verifyUserErrorMessage, 'critical')
          dispatch({
            type: API_CALL_FAILURE,
            api: USER_EMAIL_CONFIRMATION,
            error: { message: verifyUserErrorMessage },
          })
        }
      },
    })
  }
}

export function resetUserPassword(token: string, password: string, callbackPath?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_RESET_PASSWORD,
      request: () => {
        const intl = getCurrentIntl()
        return resetPassword({ token, password, luxPlusEnabled }).then(data => {
          showSnackbar(intl.formatMessage({
            id: 'ft_uUzYD5DBqdXmXzlja',
            defaultMessage: 'Your password has been successfully reset',
            description: 'Success snackbar message shown after user password reset completes',
          }), 'success')
          if (callbackPath) {
            dispatch(replace(callbackPath))
          } else {
            dispatch(replaceWithRegion('/'))
          }

          dispatch({
            type: API_CALL_SUCCESS,
            api: USER_LOGIN,
            data,
          })

          return data
        })
      },
    })
  }
}

export function forgotPassword(email: string, callbackPath?: string): AppAction {
  return {
    type: API_CALL,
    api: USER_FORGOT_PASSWORD,
    request: () => sendResetPasswordEmail(email, callbackPath),
  }
}

export function authLogout(): AppAction {
  // Run the standard USER_LOGOUT teardown for both flows; userMiddleware ends the
  // OIDC session afterwards when endOidcSession is set (so nothing is torn down
  // prematurely).
  return {
    type: API_CALL,
    api: USER_LOGOUT,
    request: () => authService.logout(),
    endOidcSession: isOidcSession(),
  }
}

function chertoCustomerUnitNeedsRefresh(state: App.State, memberId: string) {
  // Anonymous SSR omits a running Customer BK experiment, so the assignment
  // is not in state yet. The app config is what says one is registered.
  if (!canUseExperimentationOnClient() || !brandHasChertoCustomerBkExperiment(config.CHERTO_EXPERIMENTS ?? [])) {
    return false
  }

  return state.cherto.units[CHERTO_USER_ID_UNIT_TYPE] !== memberId
}

/**
 * Updates the resolved tracking ID after login by checking for an existing stitched seed ID
 * or fetching a new one from the API. This ensures consistent user identification for
 * experiment bucketing and Snowplow analytics.
 *
 * For logged-in Lab users, the login path waits briefly for seed-id (and retries
 * once after the current JS stack clears). We do not fall back to domainUserId:
 * writing the anonymous ID makes trackingIdChanged false and skips rebucket.
 * The HTTP request can outlive that wait. If the seed arrives while sync is
 * still held, we rebucket as normal. If it arrives later, we only persist the
 * cookie so the next navigation can stitch without flipping this page.
 *
 * Triggers experiment re-bucketing whenever this tab's resolvedTrackingId actually changes
 * (e.g. switching from an anonymous domainUserId to an authenticated stitched seed ID).
 * This covers both the originating tab (which fetches the seed from the API, or joins
 * the prefetch started in userMiddleware) and receiving tabs (which find the cookie
 * already written by another tab via BroadcastChannel login).
 */
export function updateResolvedTrackingId(): AppAction {
  return async(dispatch, getState) => {
    try {
      if (!canUseTrackingIdentityOnClient()) return
      const state = getState()
      const memberId = state.auth.account.memberId
      const domainUserId = getDomainUserId()
      const previousResolvedTrackingId = state.auth.resolvedTrackingId

      let stitchedSeedId = getStitchedSeedId(memberId)
      let pendingSeedRequest: Promise<string | undefined> | undefined

      if (!stitchedSeedId && memberId && config.LAB_ENABLED) {
        try {
          const result = await fetchStitchedSeedIdForLogin(memberId)
          stitchedSeedId = result.seedId
          pendingSeedRequest = result.pendingRequest
          if (stitchedSeedId) {
            persistStitchedSeedId(memberId, stitchedSeedId)
          }
        } catch(error) {
          console.error('Failed to fetch stitched seed ID:', error)
        }
      }

      // Consent can be withdrawn while the seed request is in flight.
      if (!canUseTrackingIdentityOnClient()) return

      if (memberId && config.LAB_ENABLED && !stitchedSeedId) {
        trackPostLoginExperimentSyncClientEvent('seed_id_unavailable')
        if (pendingSeedRequest) {
          persistLateStitchedSeedId(getState, memberId, pendingSeedRequest)
        }
        // Optimizely stays on the current tracking id until the stitched seed
        // arrives. Customer BK experiments still need the logged-in member id,
        // which does not depend on that seed.
        if (chertoCustomerUnitNeedsRefresh(getState(), memberId)) {
          trackPostLoginExperimentSyncClientEvent('cherto_rebucket_triggered')
          await dispatch(rebucketExperiments({ chertoOnly: true }))
        } else {
          trackPostLoginExperimentSyncClientEvent('rebucket_not_needed')
        }
        return
      }

      const resolvedTrackingId = stitchedSeedId ?? domainUserId
      if (resolvedTrackingId) {
        dispatch({
          type: SET_RESOLVED_TRACKING_ID,
          resolvedTrackingId,
        })
      }

      // Re-bucket experiments whenever this tab's resolved tracking ID changes.
      // This handles both:
      //  1. The originating tab (fetched seed from API, cookie was not yet set)
      //  2. Receiving tabs (cookie already written by another tab via BroadcastChannel login,
      //     but this tab's Redux state was still bucketed under the anonymous ID)
      const trackingIdChanged = resolvedTrackingId && resolvedTrackingId !== previousResolvedTrackingId
      if (trackingIdChanged) {
        trackPostLoginExperimentSyncClientEvent('rebucket_triggered')
        await dispatch(rebucketExperiments())
      } else if (memberId && chertoCustomerUnitNeedsRefresh(getState(), memberId)) {
        // The Snowplow tracking id is unchanged, but a Customer BK experiment
        // has not been evaluated with this member id yet.
        trackPostLoginExperimentSyncClientEvent('cherto_rebucket_triggered')
        await dispatch(rebucketExperiments({ chertoOnly: true }))
      } else {
        trackPostLoginExperimentSyncClientEvent('rebucket_not_needed')
      }
    } finally {
      // Keep calendar/search decisions blocked until LERE's login assignment is restored.
      try {
        await dispatch(refreshLabFeatureFlagsForCurrentUser({ isLogin: true }))
      } finally {
        trackPostLoginExperimentSyncClientEvent('completed')
        dispatch(endPostLoginExperimentSync())
      }
    }
  }
}

export function fetchAvailableCreditAndOpenSnackbar(currencyCode?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const memberId = state.auth.account.memberId
    if (!memberId) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABLE_CREDIT,
      request: () => getAvailableCredit(
        memberId,
        currencyCode ?? state.geo.currentCurrency,
      ).then(resp => {
        if (!resp.balance) return resp
        const {
          geo: { currentCurrency, currentRegionCode },
          router: { location: { pathname } },
          system: { headlessMode },
        } = state

        if (shouldShowCreditSnackbar(resp.balance, currentCurrency, pathname, memberId, headlessMode)) {
          const creditSnackbar = getCreditSnackbar(getCurrentIntl(), resp.balance, currentCurrency, currentRegionCode)
          showSnackbar(creditSnackbar.message, 'success', { heading: creditSnackbar.heading })
          setTimeUserLastSeenSnackbar(memberId)
        }
        return resp
      }),
      currencyCode: currencyCode ?? state.geo.currentCurrency,
    })
  }
}

export function fetchAvailableCredit(currencyCode?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const memberId = state.auth.account.memberId
    if (!memberId) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABLE_CREDIT,
      request: () => getAvailableCredit(
        memberId,
        currencyCode ?? state.geo.currentCurrency,
      ),
      currencyCode: currencyCode ?? state.geo.currentCurrency,
    })
  }
}

export function openCreditSnackbar(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const {
      geo: { currentCurrency, currentRegionCode },
      router: { location: { pathname } },
      auth: { account: { balance, memberId } },
      system: { headlessMode },
    } = state

    if (memberId && shouldShowCreditSnackbar(balance, currentCurrency, pathname, memberId, headlessMode)) {
      const creditSnackbar = getCreditSnackbar(getCurrentIntl(), balance, currentCurrency, currentRegionCode)
      showSnackbar(creditSnackbar.message, 'success', { heading: creditSnackbar.heading })
      setTimeUserLastSeenSnackbar(memberId)
    }
  }
}

export function setRecentlyUsedAirportCode(airportCode: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: API_CALL,
      api: SET_RECENTLY_USED_AIRPORT_CODE,
      // Guests have no account to save against - resolve locally so the airport is still retained in state
      request: () => state.auth.account.memberId
        ? authService.updateUserDetails({ recentlyUsedAirportCode: airportCode })
        : Promise.resolve(),
      airportCode,
    })
  }
}

export function updateAddPhonePromptedAt(addPhonePromptedAt: Date): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.auth.account.memberId) {
      dispatch({
        type: API_CALL,
        api: UPDATE_ADD_PHONE_PROMPTED,
        request: () => authService.updateAddPhonePromptedAt(addPhonePromptedAt),
        addPhonePromptedAt: addPhonePromptedAt.toISOString(),
      })
    }
  }
}

export function fetchPaymentCards(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId
    if (customerId && !isStripePaymentElementCardAvailable(state)) {
      dispatch({
        type: API_CALL,
        api: FETCH_PAYMENT_CARDS,
        request: () =>
          getSavedCardsFromStripe(customerId),
      })
    }
  }
}

export function fetchStripePaymentCards(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (isStripePaymentElementCardAvailable(state)) {
      dispatch({
        type: API_CALL,
        api: FETCH_STRIPE_PAYMENT_CARDS,
        request: () => getSavedStripeCards(state.geo.currentCurrency),
      })
    } else {
      const customerId = state.auth.account.memberId
      if (!customerId) return
      dispatch({
        type: API_CALL,
        api: FETCH_STRIPE_PAYMENT_CARDS,
        request: () => getSavedCardsOnStripe(customerId),
      })
    }
  }
}

export function fetchAgentHubAgencyStripePaymentCards(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (!isAgentHubAgencySavedCardsEnabled || !isStripePaymentElementCardAvailable(state)) return

    dispatch({
      type: API_CALL,
      api: FETCH_AGENT_HUB_AGENCY_STRIPE_PAYMENT_CARDS,
      request: () => getAgentHubAgencySavedStripeCards(state.geo.currentCurrency),
    })
  }
}

/**
 * Saves the details given for the current user in the API and updates state
 * with the newest details
 * @param userDetails The user details to save to the API
 * @param supportPhoneNumber Optional support phone number
 * @returns
 */
export function updateUserDetails(
  userDetails: Partial<App.AuthAccount>,
  supportPhoneNumber: string | null = null,
): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_USER_DETAILS,
    request: () => authService.updateUserDetails(userDetails, supportPhoneNumber),
  }
}

/**
 * Manually sync a set of user details into redux
 * Used for when you absolutely have to call the update api method yourself
 * @param userDetails The user details to sync to redux
 */
export function syncUserDetails(userDetails: Partial<App.AuthAccount>): AppAction {
  return {
    type: SYNC_USER_DETAILS,
    userDetails,
  }
}

export function updateAccountPartnership(partnershipPrefix: string, details: App.PartnershipsMap): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_ACCOUNT_PARTNERSHIP,
    request: () => authService.updatePartnershipDetails(details).then(() => details),
    partnershipPrefix,
  }
}

export function hasLegacyOrdersForCurrentUser(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (memberId && !state.auth.ledLegacyOrders.hasFetched) {
      dispatch({
        type: API_CALL,
        api: CHECK_LEGACY_ORDERS_FOR_CURRENT_USER,
        request: () => getLedLegacyOrders(memberId),
      })
    }
  }
}

export function fetchTravellerDetails(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: FETCH_CUSTOMER_DETAILS,
      request: () => getCustomerDetails(memberId),
    })
  }
}

export function deleteTravellerDetails(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: DELETE_CUSTOMER_DETAILS,
      request: () => deleteCustomerDetails(memberId),
    })
  }
}

export function updateTravellerDetails(travellerDetails: App.CustomerDetails): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_CUSTOMER_DETAILS,
    request: () => updateCustomerDetails({
      ...travellerDetails,
    }),
  }
}

export function saveCustomerSignature(signature: string): AppAction {
  return {
    type: SAVE_CUSTOMER_SIGNATURE,
    signature,
  }
}

export function deleteCustomerSignature(): AppAction {
  return {
    type: DELETE_CUSTOMER_SIGNATURE,
  }
}

export function GSILoadComplete(): AppAction {
  return {
    type: GSI_SCRIPT_LOADED,
  }
}

export function sendOTPVerificationCode(user: App.User, channel?: OtpDeliveryChannel): AppAction {
  return {
    type: API_CALL,
    api: SEND_VERIFICATION_CODE,
    request: () => authService.sendOTPVerificationCode(user, channel),
  }
}

export function verifySmsCodeAction(code: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    const requestPromise = authService
      .verifySmsCode(code)
      .then(({ accessToken, token }) =>
        authService.me(accessToken, regionCode, luxPlusEnabled).then((account) => ({
          accessToken: accessToken ?? state.auth.accessToken,
          account,
          token,
        })),
      )

    dispatch({
      type: API_CALL,
      api: VERIFY_SMS_CODE,
      request: () => requestPromise,
    })

    return requestPromise
  }
}

export function validateOTPVerificationCode(user: App.User, code: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.validateOTPVerificationCode(user, code, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'otp',
    })
  }
}

export function sendSignUpOTPCode(phoneNumber: App.PhoneNumber, channel?: OtpDeliveryChannel): AppAction {
  return {
    type: API_CALL,
    api: SEND_SIGNUP_OTP_CODE,
    request: () => authService.sendSignUpOTPCode(phoneNumber, channel),
  }
}

export function verifySignUpOTPCode(phoneNumber: App.PhoneNumber, code: string): AppAction {
  return {
    type: API_CALL,
    api: VERIFY_SIGNUP_OTP_CODE,
    request: () => authService.verifySignUpOTPCode(phoneNumber, code),
  }
}

export function validateRegistration(userData: Partial<App.AuthAccount> & { password: string, marketingOptIn?: boolean }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { marketingOptIn: _marketingOptIn, ...registerData } = userData

    dispatch({
      type: API_CALL,
      api: VALIDATE_REGISTRATION,
      request: () => authService.validateRegistration(registerData, state.geo.currentRegionCode),
    })
  }
}

export function registerWithOTP(userData: Partial<App.AuthAccount> & { password: string, marketingOptIn?: boolean }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { marketingOptIn, ...accountFields } = userData

    const registerData = {
      ...accountFields,
      utmAdgroup: state.utm.adgroup,
      utmCampaign: state.utm.campaign,
      utmContent: state.utm.content,
      utmMedium: state.utm.medium,
      utmSource: state.utm.source,
      utmTerm: state.utm.term,
    }

    dispatch({
      type: API_CALL,
      api: USER_REGISTRATION,
      source: 'email',
      marketingOptIn,
      request: () => authService.registerWithOTP(registerData, state.geo.currentRegionCode),
    })
  }
}

export function setCustomerValue(price: number): AppAction {
  const value = price >= HIGH_VALUE_OFFER_AMOUNT ? 'highValue' : price >= MID_VALUE_OFFER_AMOUNT ? 'midValue' : undefined
  return {
    type: SET_CUSTOMER_VALUE,
    value,
  }
}

/**
 * Call to clear any existing auth errors
 */
export function clearAuthError(): AppAction {
  return {
    type: CLEAR_AUTH_ERROR,
  }
}

interface CustomerTravellersFetchOptions {
  refetch?: boolean
}
export function fetchTravelCompanions(options: CustomerTravellersFetchOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!state.auth.travelCompanions.initial && !options.refetch) return
    if (state.auth.travelCompanions.fetching && !options.refetch) return
    if (state.auth.travelCompanions.error && !options.refetch) return

    dispatch({
      type: API_CALL,
      api: FETCH_CUSTOMER_TRAVELLERS,
      request: () => requestTravelCompanions(),
    })
  }
}

export function destroyCustomerTravellerDetails(id: string): AppAction {
  return (dispatch) => {
    if (!id) { return }
    dispatch({
      type: API_CALL,
      api: DELETE_CUSTOMER_TRAVELLER,
      id,
      request: () => deleteCustomerTraveller(id),
    })
  }
}

export function createCustomerTravellerDetails(
  customerTraveller: Omit<App.TravelCompanion, 'id'>,
  options: {
    onCreated?: (companion: App.TravelCompanion) => void
  } = {},
): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SUBMIT_CUSTOMER_TRAVELLER,
      request: () => createCustomerTraveller(customerTraveller).then(companion => {
        options.onCreated?.(companion)
        dispatch(fetchTravelCompanions({ refetch: true }))
        return companion
      }),
    })
  }
}

export function updateCustomerTravellerDetails(customerTraveller: App.TravelCompanion): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SUBMIT_CUSTOMER_TRAVELLER,
      request: () => updateCustomerTraveller(customerTraveller).then(companion => {
        dispatch(fetchTravelCompanions({ refetch: true }))
        return companion
      }),
    })
  }
}

export function resetCustomerTravellerSubmission(): AppAction {
  return {
    type: RESET_CUSTOMER_TRAVELLER_SUBMISSION,
  }
}

export function fetchSimilarAccounts(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: FETCH_SIMILAR_ACCOUNTS,
      request: () => getSimilarAccounts(),
    })
  }
}
